/** Back office → Campaigns (C12): Meta Click-to-WhatsApp campaigns, global ROI, creation from an offer. */

import {
  type AdBudgetKindEnum,
  type AdObjectiveEnum,
  useAdsCampaignsCreate,
  useAdsCampaignsFromOfferCreate,
  useAdsCampaignsList,
  useAdsCampaignsRoiRetrieve,
  useWebsiteOffersList,
} from "@agencyos/api-client";
import { parseXaf } from "@agencyos/core";
import { Badge, Button, GlassCard, GlassSheet, SelectField, Skeleton, TextField } from "@agencyos/ui-web";
import { Megaphone, Plus } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Page } from "../../components/AppShell";
import { localized } from "../../components/TranslatedFields";
import { fieldErrors } from "../../lib/settings";
import { dateText, todayInDouala } from "../billing/shared";
import "./ads.css";
import { MetricsGrid, ModeNotice, STATUS_TONE, splitList, useAdsLive, useRefreshAds } from "./shared";

const OBJECTIVES: AdObjectiveEnum[] = ["engagement", "leads"];

function amountOf(value: string): number {
  try {
    return parseXaf(value);
  } catch {
    return 0;
  }
}
const BUDGET_KINDS: AdBudgetKindEnum[] = ["daily", "lifetime"];

function NewCampaignSheet({ open, offerId, onClose }: { open: boolean; offerId: string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const refresh = useRefreshAds();
  const offers = useWebsiteOffersList(undefined, { query: { enabled: open } });
  const fromOffer = useAdsCampaignsFromOfferCreate();
  const create = useAdsCampaignsCreate();
  const [offer, setOffer] = useState(offerId);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [objective, setObjective] = useState<AdObjectiveEnum>("engagement");
  const [kind, setKind] = useState<AdBudgetKindEnum>("daily");
  const [budget, setBudget] = useState("");
  const [start, setStart] = useState(todayInDouala());
  const [end, setEnd] = useState("");
  const [cities, setCities] = useState("");
  const [interests, setInterests] = useState("");
  const [ageMin, setAgeMin] = useState("18");
  const [ageMax, setAgeMax] = useState("65");
  useEffect(() => setOffer(offerId), [offerId]);

  const published = (offers.data?.status === 200 ? offers.data.data.results : []).filter((o) => o.status === "published");
  const pending = fromOffer.isPending || create.isPending;
  const errors = { ...fieldErrors(fromOffer.error), ...fieldErrors(create.error) };
  const amount = amountOf(budget);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!(amount > 0)) return;
    const common = {
      objective,
      budget_kind: kind,
      budget_xaf: amount,
      start_date: start,
      end_date: end || null,
      cities: splitList(cities),
      interests: splitList(interests),
      age_min: Number(ageMin) || 18,
      age_max: Number(ageMax) || 65,
    };
    const done = (id: string) => {
      refresh();
      onClose();
      navigate(`/ads/${id}`);
    };
    if (offer) {
      fromOffer.mutate(
        { data: { offer, language: i18n.language === "en" ? "en" : "fr", ...common } },
        { onSuccess: (r) => r.status === 201 && done(r.data.id) },
      );
    } else {
      create.mutate({ data: { name, message, ...common } }, { onSuccess: (r) => r.status === 201 && done(r.data.id) });
    }
  };

  return (
    <GlassSheet open={open} title={t("ads.form.title")} onClose={onClose}>
      <form onSubmit={onSubmit} className="stack" noValidate>
        <p className="muted">{t("ads.form.intro")}</p>
        <SelectField
          label={t("ads.form.offer")}
          hint={offer ? t("ads.form.offerHint") : undefined}
          value={offer}
          error={errors.offer}
          options={[
            { value: "", label: t("ads.form.noOffer") },
            ...published.map((o) => ({ value: o.id, label: `${o.code} — ${localized(o.name, i18n.language)}` })),
          ]}
          onChange={(e) => setOffer(e.target.value)}
        />
        {offer ? null : (
          <>
            <TextField label={t("ads.form.name")} value={name} error={errors.name} onChange={(e) => setName(e.target.value)} />
            <TextField label={t("ads.form.message")} value={message} error={errors.message} onChange={(e) => setMessage(e.target.value)} />
          </>
        )}
        <SelectField
          label={t("ads.form.objective")}
          value={objective}
          options={OBJECTIVES.map((o) => ({ value: o, label: t(`ads.objective.${o}`) }))}
          onChange={(e) => setObjective(e.target.value as AdObjectiveEnum)}
        />
        <div className="grid-2">
          <SelectField
            label={t("ads.form.budgetKind")}
            value={kind}
            options={BUDGET_KINDS.map((k) => ({ value: k, label: t(`ads.budgetKind.${k}`) }))}
            onChange={(e) => setKind(e.target.value as AdBudgetKindEnum)}
          />
          <TextField
            label={t("ads.form.budget")}
            hint={t("ads.form.budgetHint")}
            inputMode="numeric"
            value={budget}
            error={errors.budget_xaf}
            onChange={(e) => setBudget(e.target.value)}
          />
          <TextField label={t("ads.form.start")} type="date" value={start} error={errors.start_date} onChange={(e) => setStart(e.target.value)} />
          <TextField
            label={t("ads.form.end")}
            hint={t("ads.form.endHint")}
            type="date"
            value={end}
            error={errors.end_date}
            onChange={(e) => setEnd(e.target.value)}
          />
        </div>
        <TextField label={t("ads.form.cities")} hint={t("ads.form.citiesHint")} value={cities} error={errors.cities} onChange={(e) => setCities(e.target.value)} />
        <TextField
          label={t("ads.form.interests")}
          hint={t("ads.form.interestsHint")}
          value={interests}
          error={errors.interests}
          onChange={(e) => setInterests(e.target.value)}
        />
        <div className="grid-2">
          <TextField label={t("ads.form.ageMin")} inputMode="numeric" value={ageMin} error={errors.age_min} onChange={(e) => setAgeMin(e.target.value)} />
          <TextField label={t("ads.form.ageMax")} inputMode="numeric" value={ageMax} error={errors.age_max} onChange={(e) => setAgeMax(e.target.value)} />
        </div>
        {fromOffer.isError || create.isError ? (
          <p role="alert" className="ko">
            {Object.values(errors)[0] ?? t("common.error")}
          </p>
        ) : null}
        <div className="row">
          <Button type="submit" disabled={pending || !(amount > 0) || (!offer && (!name || !message))}>
            {pending ? t("common.loading") : t("ads.form.create")}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </GlassSheet>
  );
}

export function AdsPage() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const offerId = params.get("offer") ?? "";
  const [creating, setCreating] = useState(Boolean(offerId));
  const live = useAdsLive();
  const campaigns = useAdsCampaignsList();
  const roi = useAdsCampaignsRoiRetrieve();
  const rows = campaigns.data?.status === 200 ? campaigns.data.data.results : [];
  const report = roi.data?.status === 200 ? roi.data.data : undefined;

  const close = () => {
    setCreating(false);
    if (offerId) setParams({}, { replace: true });
  };

  return (
    <Page
      title={t("ads.title")}
      description={t("ads.intro")}
      actions={
        <Button icon={<Plus size={16} aria-hidden="true" />} onClick={() => setCreating(true)}>
          {t("ads.new")}
        </Button>
      }
    >
      <ModeNotice live={live} />
      <GlassCard className="stack" aria-labelledby="ads-roi-title">
        <h2 id="ads-roi-title">{t("ads.roi.title")}</h2>
        <p className="muted">
          {t("ads.roi.intro")} {report ? (report.test ? t("ads.roi.testScope") : t("ads.roi.liveScope")) : null}
        </p>
        {report ? <MetricsGrid metrics={report.totals} label={t("ads.roi.title")} /> : <Skeleton style={{ height: 90 }} />}
        {report?.offers.length ? (
          <div className="table-wrap">
            <table className="table" aria-label={t("ads.roi.byOffer")}>
              <thead>
                <tr>
                  <th>{t("ads.roi.byOffer")}</th>
                  <th className="num">{t("ads.roi.spend")}</th>
                  <th className="num">{t("ads.roi.leads")}</th>
                  <th className="num">{t("ads.roi.clients")}</th>
                  <th className="num">{t("ads.roi.revenue")}</th>
                </tr>
              </thead>
              <tbody>
                {report.offers.map((o) => (
                  <tr key={o.offer}>
                    <td>{o.code}</td>
                    <td className="num nowrap">{o.spend_text}</td>
                    <td className="num">{o.leads}</td>
                    <td className="num">{o.clients}</td>
                    <td className="num nowrap">{o.revenue_text}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </GlassCard>
      <GlassCard className="stack">
        {campaigns.isPending ? <Skeleton style={{ height: 160 }} /> : null}
        {campaigns.isError ? (
          <p role="alert" className="ko">
            {t("common.error")}
          </p>
        ) : null}
        {campaigns.data && rows.length === 0 ? (
          <div className="stack contract-empty">
            <Megaphone size={28} aria-hidden="true" className="muted" />
            <p className="muted">{t("ads.list.empty")}</p>
          </div>
        ) : null}
        {rows.length ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("ads.list.name")}</th>
                  <th>{t("ads.list.offer")}</th>
                  <th>{t("ads.list.status")}</th>
                  <th className="num">{t("ads.list.budget")}</th>
                  <th>{t("ads.list.created")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link className="link" to={`/ads/${c.id}`}>
                        {c.name}
                      </Link>{" "}
                      {c.is_test ? <Badge>{t("ads.list.testBadge")}</Badge> : null}
                    </td>
                    <td>{c.offer_code || "—"}</td>
                    <td>
                      <Badge tone={STATUS_TONE[c.status]}>{t(`ads.status.${c.status}`)}</Badge>
                    </td>
                    <td className="num nowrap">
                      {c.budget_text} <span className="muted">· {t(`ads.budgetKind.${c.budget_kind ?? "daily"}`)}</span>
                    </td>
                    <td className="nowrap">{dateText(c.created_at, i18n.language)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </GlassCard>
      <NewCampaignSheet open={creating} offerId={offerId} onClose={close} />
    </Page>
  );
}
